import { useAppDispatch } from "@store/store-helper";
import { FaroTable } from "@components/common/faro-table/faro-table";
import { useEffect, useMemo } from "react";
import { resetTableState, setTableType } from "@store/table/table-slice";
import { TableType } from "@utils/track-event/track-event-list";
import { getTeamsColumns } from "@components/table/teams/teams-columns";
import { GroupBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { TeamsHeaders } from "@components/table/teams/teams-table-types";
import { useDateTime } from "@hooks/use-date-time";
import { useTeamUtils } from "@hooks/use-team-utils";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { useMembers } from "@hooks/use-members";
import { getMembersMapById } from "@utils/member-utils";
import { HIDE_ON_HOVER_CLASS } from "@utils/ui-utils";

interface Props extends BaseCompanyIdProps {
  /** The teams that is shown in the table */
  teams: SphereDashboardAPITypes.ITeam[];

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** All the columns that we want to show. The order defines the order of the columns */
  requiredColumns: TeamsHeaders[];

  /** List of all the bulk actions and callbacks */
  bulkActionButtons?: GroupBulkButtonComponents;
}

/** Listing all the teams of a company */
export function TeamsTable({
  companyId,
  teams,
  isLoading,
  requiredColumns,
  bulkActionButtons,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { formatDate } = useDateTime();
  const { openTeamDetails } = useTeamUtils();
  const { companyMembers } = useMembers();
  const companyMembersMap = getMembersMapById(companyMembers);

  // Make sure the correct table type is set in the store and reset the bulk action state after unmounting
  useEffect(() => {
    dispatch(setTableType(TableType.teams));

    return () => {
      dispatch(resetTableState());
    };
  }, [dispatch]);

  const teamsColumns = useMemo(
    () =>
      requiredColumns.map(
        (requiredColumn) =>
          getTeamsColumns({
            companyId,
            formatDate,
            companyMembersMap,
          })[requiredColumn]
      ),
    [companyId, formatDate, companyMembersMap, requiredColumns]
  );

  return (
    <FaroTable
      entities={teams}
      uniqueIdKey={"id"}
      isLoading={isLoading}
      columns={teamsColumns}
      bulkActionButtons={bulkActionButtons}
      rowHeight={70}
      sx={{
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },

        [`& .MuiDataGrid-row:hover .${HIDE_ON_HOVER_CLASS}`]: {
          display: "none",
        },
      }}
      onRowClick={(teamId) => openTeamDetails({ teamId: teamId.toString() })}
      tableType={TableType.teams}
    />
  );
}
